// Advanced settings → Attendance policy for one department (admin /
// superadmin). Reached through AdvancedSettings, which does the warning and
// the unlock.
//
// A policy is configuration on top of the registers: it decides what the
// Take Attendance form offers and how views count. It never changes a
// register. Without any policy, everything works as today.
//
// One wide window with two views — where policies apply (the department and
// its programmes, searchable and paged) and the institute's policies — and,
// while one is being edited, the editor in its place:
//   Form & counting   the six settings as plain questions
//   Report columns    extra columns and header cells with formulas
//                     (PolicyReportFields)
//
//   1. where policies apply: this department and each of its programmes —
//      pick a policy (or inherit), enforce (lower levels can't override)
//   2. the institute's policies: start one from a preset, or edit one —
//      every save is a new version from a date, old versions are kept
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ClipboardCheck, Loader2, Lock, Pencil, Plus, Search, Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { WorkspaceDialog } from "@/components/WorkspaceDialog";
import { DataPager, usePagedList } from "@/components/DataPager";
import { RowActions } from "@/components/RowActions";
import { fieldProblems } from "@/lib/policyFormula";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useInstituteLabels } from "@/lib/instituteLabels";
import { type ClassroomSummary, type DepartmentProfile, type Programme, listDepartmentClassrooms, listProgrammes } from "./structureApi";
import { PolicyReportFields } from "./PolicyReportFields";
import { PolicyPreviewPanel } from "./PolicyPreviewPanel";
import { type AssignChange, INHERIT, PolicyScopeBrowser } from "./PolicyScopeBrowser";
import {
    type Assignment, type Policy, type PolicyList, type PolicySettings, type PolicySlot,
    PolicyError, archivePolicy, assignPolicy, listPolicies, savePolicy, unassignPolicy,
} from "./policyApi";


// Empty report lists are left out of a save, so a policy without formulas
// also saves on a server from before they existed.
const forServer = (settings: PolicySettings) => {
    const { customColumns, headerFields, ...rest } = settings;
    return {
        ...rest,
        ...(customColumns.length ? { customColumns } : {}),
        ...(headerFields.length ? { headerFields } : {}),
    };
};

// A copy safe to edit, with the report lists present even on settings saved
// before they existed.
// (A policy saved before the yes/no had `visibility`; only "hidden" meant no.)
const complete = (settings: PolicySettings): PolicySettings => {
    const { visibility, ...rest } = structuredClone(settings) as PolicySettings & { visibility?: string };
    return {
        ...rest,
        studentsSeeAttendance: typeof rest.studentsSeeAttendance === "boolean" ? rest.studentsSeeAttendance : visibility !== "hidden",
        customColumns: rest.customColumns || [],
        headerFields: rest.headerFields || [],
    };
};
const today = () => new Date().toISOString().slice(0, 10);

type Draft = { policyKey?: string; basedOn?: string; name: string; settings: PolicySettings; effectiveFrom: string };

type Props = { department: DepartmentProfile | null; onBack: () => void; onClose: () => void };

export function AttendancePolicyPanel({ department, onBack, onClose }: Props) {
    const L = useInstituteLabels();
    const deptName = department
        ? (department.displayName === department.departmentName ? department.departmentName.replace(/_/g, " ") : department.displayName)
        : "";

    const [ data, setData ] = useState<PolicyList | null>(null);
    const [ programmes, setProgrammes ] = useState<Programme[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ busy, setBusy ] = useState<string | null>(null);
    const [ error, setError ] = useState<{ message: string; details: string[] } | null>(null);
    const [ draft, setDraft ] = useState<Draft | null>(null);
    const [ view, setView ] = useState<"applies" | "policies">("applies");
    const [ editorTab, setEditorTab ] = useState<"form" | "report" | "preview">("form");

    const reportHasProblems = useMemo(() => {
        if (!draft) return false;
        const p = fieldProblems(draft.settings.customColumns, draft.settings.headerFields);
        return [ ...p.columns, ...p.headers ].some(Boolean);
    }, [ draft ]);

    const fail = (err: any) => setError({ message: err?.message || "Something went wrong.", details: err instanceof PolicyError ? err.details : [] });

    const load = useCallback(async () => {
        if (!department) return;
        setLoading(true);
        setError(null);
        try {
            const [ list, progs ] = await Promise.all([ listPolicies(), listProgrammes(department.departmentName) ]);
            setData(list);
            setProgrammes(progs);
        } catch (err) {
            fail(err);
        } finally {
            setLoading(false);
        }
    }, [ department ]);

    useEffect(() => {
        setData(null); setDraft(null); setError(null);
    }, [ department ]);

    useEffect(() => { if (!data) load(); }, [ data, load ]);

    const policyName = useMemo(() => {
        const m = new Map((data?.policies || []).map(p => [ p.policyKey, p ]));
        return (key: string) => m.get(key) ? `${m.get(key)!.name} (v${m.get(key)!.version})` : key;
    }, [ data ]);

    if (!department) return null;


    // One or many changes (bulk on classrooms), then one reload. Stops at the
    // first failure and says which; what was done before it stays done.
    const applyChanges = async (changes: AssignChange[]) => {
        if (changes.length === 0) return;
        setBusy("assign");
        setError(null);
        let done = 0;
        try {
            for (const c of changes) {
                if (c.policyKey === INHERIT) await unassignPolicy(c.level, c.targetId);
                else await assignPolicy({ level: c.level, targetId: c.targetId, policyKey: c.policyKey, enforced: c.enforced });
                done++;
            }
        } catch (err) {
            fail(err);
            if (changes.length > 1) setError(e => e && { ...e, message: `${e.message} (${done} of ${changes.length} were saved)` });
        } finally {
            await load();
            setBusy(null);
        }
    };

    const startFromPreset = (key: string) => {
        const preset = data?.presets.find(p => p.key === key);
        if (!preset) return;
        setDraft({ basedOn: key, name: `${deptName} – ${preset.name}`, settings: complete(preset.settings), effectiveFrom: today() });
        setEditorTab("form");
        setError(null);
    };

    const editPolicy = (p: Policy) => {
        setDraft({ policyKey: p.policyKey, name: p.name, settings: complete(p.settings), effectiveFrom: today() });
        setEditorTab("form");
        setError(null);
    };

    const handleSave = async () => {
        if (!draft) return;
        setBusy("save");
        setError(null);
        try {
            await savePolicy({
                ...(draft.policyKey ? { policyKey: draft.policyKey } : { basedOn: draft.basedOn }),
                name: draft.name, settings: forServer(draft.settings), effectiveFrom: draft.effectiveFrom,
            });
            setDraft(null);
            await load();
        } catch (err) {
            fail(err);
        } finally {
            setBusy(null);
        }
    };

    const handleArchive = async (p: Policy) => {
        setBusy(`archive:${p.policyKey}`);
        setError(null);
        try {
            await archivePolicy(p.policyKey);
            await load();
        } catch (err) {
            fail(err);
        } finally {
            setBusy(null);
        }
    };

    const policies = data?.policies || [];
    const usedIn = (key: string) => (data?.assignments || []).filter(a => a.policyKey === key).length;

    const errorBox = error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <p className="flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {error.message}</p>
            {error.details.length > 0 && <ul className="ml-6 mt-1 list-disc">{error.details.map(d => <li key={d}>{d}</li>)}</ul>}
        </div>
    );

    // ── Editing one policy: the whole window is the editor ──────────────────
    if (draft) {
        return (
            <WorkspaceDialog
                icon={ClipboardCheck}
                back={{ label: "Attendance policy", onClick: () => { if (busy !== "save") { setDraft(null); setError(null); } } }}
                title={draft.policyKey ? `Edit · ${draft.name || "policy"}` : "New attendance policy"}
                description={draft.policyKey ? "Saving makes a new version from the date below; earlier versions are kept." : `Starts from “${data?.presets.find(p => p.key === draft.basedOn)?.name ?? "a preset"}”.`}
                headerExtra={
                    <Tabs value={editorTab} onValueChange={(v) => setEditorTab(v as typeof editorTab)}>
                        <TabsList>
                            <TabsTrigger value="form">Form & counting</TabsTrigger>
                            <TabsTrigger value="report">
                                Report columns
                                {(draft.settings.customColumns.length + draft.settings.headerFields.length) > 0 && (
                                    <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-[10px]">{draft.settings.customColumns.length + draft.settings.headerFields.length}</Badge>
                                )}
                            </TabsTrigger>
                            <TabsTrigger value="preview">Preview on real data</TabsTrigger>
                        </TabsList>
                    </Tabs>
                }
                preventClose={busy === "save"}
                onClose={onClose}
                footer={
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-wrap items-end gap-3">
                            <div className="w-72 space-y-1">
                                <Label className="text-[11px] text-muted-foreground">Policy name</Label>
                                <Input className="h-8" value={draft.name} maxLength={80} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                            </div>
                            <div className="w-40 space-y-1">
                                <Label className="text-[11px] text-muted-foreground">Applies from</Label>
                                <Input className="h-8" type="date" value={draft.effectiveFrom} onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })} />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" onClick={() => { setDraft(null); setError(null); }} disabled={busy === "save"}>Cancel</Button>
                            <Button onClick={handleSave} disabled={busy === "save" || !draft.name.trim() || reportHasProblems}>
                                {busy === "save" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</> : (draft.policyKey ? "Save new version" : "Create policy")}
                            </Button>
                        </div>
                    </div>
                }
            >
                {errorBox}
                {reportHasProblems && editorTab === "form" && (
                    <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        Some report columns need fixing before you can save — see “Report columns”.
                    </p>
                )}
                {editorTab === "preview" ? (
                    <PolicyPreviewPanel
                        settings={forServer(draft.settings)}
                        departmentName={department.departmentName}
                        deptName={deptName}
                        programmes={programmes}
                        reportHasProblems={reportHasProblems}
                    />
                ) : editorTab === "form"
                    ? <PolicyFormSettings draft={draft} onChange={setDraft} />
                    : (
                        <PolicyReportFields
                            columns={draft.settings.customColumns}
                            headers={draft.settings.headerFields}
                            minimumPercent={draft.settings.minimumPercent}
                            slotsPerDay={Math.max(1, draft.settings.slots.length)}
                            onChange={(customColumns, headerFields) => setDraft({ ...draft, settings: { ...draft.settings, customColumns, headerFields } })}
                        />
                    )}
            </WorkspaceDialog>
        );
    }

    // ── Overview: where it applies · the institute's policies ──────────────
    return (
        <WorkspaceDialog
            icon={ClipboardCheck}
            back={{ label: "Advanced settings", onClick: () => { if (!busy) onBack(); } }}
            title={`Attendance policy · ${deptName}`}
            description="A policy decides what the attendance form asks for and how attendance is counted. Registers already taken never change."
            headerExtra={
                <Tabs value={view} onValueChange={(v) => setView(v as typeof view)}>
                    <TabsList>
                        <TabsTrigger value="applies">Where it applies</TabsTrigger>
                        <TabsTrigger value="policies">Policies <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-[10px]">{policies.length}</Badge></TabsTrigger>
                    </TabsList>
                </Tabs>
            }
            size="full"
            bodyClassName={view === "applies" ? "lg:overflow-hidden" : undefined}
            preventClose={!!busy}
            onClose={onClose}
        >
            {errorBox}
            {loading && !data ? (
                <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : view === "applies" ? (
                <PolicyScopeBrowser
                    department={department}
                    deptName={deptName}
                    programmes={programmes}
                    policies={policies}
                    assignments={data?.assignments || []}
                    policyName={policyName}
                    busy={!!busy}
                    onAssign={applyChanges}
                />
            ) : (
                <div className="space-y-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-muted-foreground">The institute's attendance policies. Every save is a new version from a date.</p>
                        <Select value="" onValueChange={startFromPreset}>
                            <SelectTrigger className="h-9 w-full text-sm sm:w-72"><Plus className="h-4 w-4" /><SelectValue placeholder="New policy from a preset…" /></SelectTrigger>
                            <SelectContent>
                                {(data?.presets || []).map(p => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {policies.length === 0 ? (
                        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">No policies yet. Start one from a preset.</p>
                    ) : (
                        <div className="overflow-hidden rounded-lg border">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/30">
                                        <TableHead className="pl-4">Policy</TableHead>
                                        <TableHead>Version</TableHead>
                                        <TableHead>Applies from</TableHead>
                                        <TableHead>Report columns</TableHead>
                                        <TableHead>Used in</TableHead>
                                        <TableHead className="w-12 pr-3"><span className="sr-only">Actions</span></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {policies.map(p => (
                                        <TableRow key={p.policyKey} className="cursor-pointer" onClick={() => editPolicy(p)}>
                                            <TableCell className="pl-4 font-medium">{p.name}</TableCell>
                                            <TableCell><Badge variant="secondary">v{p.version}</Badge></TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{p.effectiveFrom}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {(p.settings.customColumns?.length || 0) + (p.settings.headerFields?.length || 0) || "—"}
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{usedIn(p.policyKey) ? `${usedIn(p.policyKey)} place(s)` : "Not applied"}</TableCell>
                                            <TableCell className="pr-3 text-right">
                                                <RowActions label={p.name} busy={busy === `archive:${p.policyKey}`} actions={[
                                                    { label: "Edit (new version)", icon: Pencil, onSelect: () => editPolicy(p) },
                                                    { label: "Archive", icon: Trash2, onSelect: () => handleArchive(p), destructive: true },
                                                ]} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            )}
        </WorkspaceDialog>
    );
}

// ── Form & counting: the six settings as plain questions ───────────────────
function Choice<T extends string>({ value, options, onChange }: { value: T; options: [ T, string ][]; onChange: (v: T) => void }) {
    return (
        <div className="flex flex-wrap gap-1.5">
            {options.map(([ v, label ]) => (
                <Button key={v} type="button" size="sm" variant={value === v ? "default" : "outline"} className="h-8 text-xs" onClick={() => onChange(v)}>{label}</Button>
            ))}
        </div>
    );
}

function Setting({ title, help, children }: { title: string; help?: string; children: ReactNode }) {
    return (
        <div className="grid gap-3 border-b py-5 last:border-b-0 md:grid-cols-[280px_1fr]">
            <div>
                <p className="text-sm font-medium">{title}</p>
                {help && <p className="mt-0.5 text-xs text-muted-foreground">{help}</p>}
            </div>
            <div>{children}</div>
        </div>
    );
}

function PolicyFormSettings({ draft, onChange }: { draft: Draft; onChange: (d: Draft) => void }) {
    const L = useInstituteLabels();
    const s = draft.settings;
    const set = (patch: Partial<PolicySettings>) => onChange({ ...draft, settings: { ...s, ...patch } });
    const setSlot = (i: number, patch: Partial<PolicySlot>) => set({ slots: s.slots.map((x, j) => (j === i ? { ...x, ...patch } : x)) });

    return (
        <div className="max-w-4xl">
            <Setting title={`Does a register need a ${L.subject.toLowerCase()}?`}>
                <Choice value={s.subject} onChange={(v) => set({ subject: v })}
                    options={[ [ "select", `${L.subject}-wise — faculty must pick a ${L.subject.toLowerCase()}` ], [ "none", `No ${L.subject.toLowerCase()} — whole ${L.classroom.toLowerCase()}` ] ]} />
            </Setting>

            <Setting title="Fixed slots in a day" help="Leave empty for a free “period” field, as today.">
                <div className="space-y-2">
                    {s.slots.map((slot, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2">
                            <Input className="h-8 w-44" placeholder="Name, e.g. Morning" value={slot.name} onChange={(e) => setSlot(i, { name: e.target.value })} />
                            <Input className="h-8 w-28" type="time" value={slot.start || ""} onChange={(e) => setSlot(i, { start: e.target.value || undefined })} />
                            <span className="text-xs text-muted-foreground">to</span>
                            <Input className="h-8 w-28" type="time" value={slot.end || ""} onChange={(e) => setSlot(i, { end: e.target.value || undefined })} />
                            <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Remove slot" onClick={() => set({ slots: s.slots.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>
                        </div>
                    ))}
                    {s.slots.length < 12 && (
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => set({ slots: [ ...s.slots, { name: "" } ] })}><Plus className="h-3.5 w-3.5" /> Add slot</Button>
                    )}
                </div>
            </Setting>

            <Setting title="Duplicate warning" help={`Warn when a register for the same ${L.classroom.toLowerCase()}, date and slot already exists. It never blocks.`}>
                <Switch checked={s.duplicateWarning} onCheckedChange={(v) => set({ duplicateWarning: v })} />
            </Setting>

            <Setting title="Show attendance in">
                <Choice value={s.counting} onChange={(v) => set({ counting: v })}
                    options={[ [ "sessions", "Classes (as today)" ], [ "days", "Days (each class is its share of the day)" ] ]} />
            </Setting>

            <Setting title="Students see attendance?"
                help={`No: students of these ${L.classrooms.toLowerCase()} don't see their attendance or history. Attendance is still counted, so switching back to Yes shows everything at once.`}>
                <Choice value={s.studentsSeeAttendance ? "yes" : "no"} onChange={(v) => set({ studentsSeeAttendance: v === "yes" })}
                    options={[ [ "yes", "Yes" ], [ "no", "No" ] ]} />
            </Setting>

            <Setting title="Minimum attendance %" help="Empty = no minimum. Report formulas can use it as minimum_percent.">
                <Input className="h-8 w-28" type="number" min={0} max={100} value={s.minimumPercent ?? ""}
                    onChange={(e) => set({ minimumPercent: e.target.value === "" ? null : Number(e.target.value) })} />
            </Setting>
        </div>
    );
}
